/**
 * 👁️ PermissionsViewModal - Modal de Visualização de Permissões
 * 
 * Exibe as permissões de um grupo de forma clara e legível,
 * convertendo permissões técnicas em descrições amigáveis.
 * 
 * @author Pagmus Team
 * @date 2025-11-06
 */

import React from 'react';
import { Modal, Badge } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { getReadablePermissions, countActivePermissions, hasFullAccess } from '../../utils/permissionDescriptions';
import './PermissionsViewModal.scss';

const PermissionsViewModal = ({ show, onHide, group }) => {
  if (!group) return null;

  // Parse permissions se vier como string
  const permissions = typeof group.permissions === 'string' 
    ? JSON.parse(group.permissions) 
    : group.permissions;

  const readablePermissions = getReadablePermissions(permissions);
  const totalPermissions = countActivePermissions(permissions);
  const isFullAccess = hasFullAccess(permissions);

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="lg" 
      centered
      className="permissions-view-modal"
    >
      <Modal.Header closeButton>
        <div className="modal-header-content">
          <div className="header-title">
            <Icon icon="mdi:shield-check" width={28} height={28} className="me-2" />
            <span style={{ fontSize: '1.25rem', fontWeight: 600 }}>
              Permissões do Grupo
            </span>
          </div>
          <div className="group-info">
            <span style={{ fontSize: '1rem', fontWeight: 500, color: '#495057' }}>
              {group.name}
            </span>
            {group.description && (
              <span style={{ fontSize: '0.875rem', color: '#6c757d', marginTop: '4px' }}>
                {group.description}
              </span>
            )}
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        {/* Badge de resumo */}
        <div className="permissions-summary">
          {isFullAccess && (
            <Badge bg="success" className="me-2 text-contrast">
              <Icon icon="mdi:crown" width={16} className="me-1" />
              <span>Acesso Completo</span>
            </Badge>
          )}
          <Badge bg="info">
            <Icon icon="mdi:key" width={16} className="me-1 text-contrast" />
            <span>
               {totalPermissions} {totalPermissions === 1 ? 'Permissão Ativa' : 'Permissões Ativas'}
            </span>
          </Badge>
        </div>

        {/* Lista de permissões por módulo */}
        {readablePermissions.length === 0 ? (
          <div className="empty-permissions">
            <Icon icon="mdi:lock-off" width={48} className="mb-3" style={{ color: '#adb5bd' }} />
            <span style={{ fontSize: '1rem', color: '#6c757d' }}>
              Nenhuma permissão configurada para este grupo
            </span>
          </div>
        ) : (
          <div className="permissions-list">
            {readablePermissions.map((module, index) => (
              <div key={module.module} className="permission-module">
                {/* Cabeçalho do módulo */}
                <div className="module-header">
                  <Icon icon={module.icon} width={24} height={24} className="module-icon" />
                  <div className="module-info">
                    <span className="module-title" style={{ fontSize: '1rem', fontWeight: 600 }}>
                      {module.title}
                    </span>
                    <span className="module-description" style={{ fontSize: '0.875rem', color: '#6c757d' }}>
                      {module.description}
                    </span>
                  </div>
                </div>

                {/* Lista de ações do módulo */}
                <ul className="actions-list">
                  {module.actions.map((action, actionIndex) => (
                    <li key={`${module.module}-${action.key}`} className="action-item">
                      <Icon icon="mdi:check-circle" width={18} className="check-icon" />
                      <span style={{ fontSize: '0.9375rem' }}>{action.description}</span>
                    </li>
                  ))}
                </ul>

                {/* Divisor entre módulos (exceto no último) */}
                {index < readablePermissions.length - 1 && <div className="module-divider" />}
              </div>
            ))}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <button
          onClick={onHide}
          className="btn btn-secondary"
          style={{ padding: '8px 24px' }}
        >
          Fechar
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default PermissionsViewModal;

